"use client"

import { listProducts } from "@lib/data/products"
import { HttpTypes } from "@medusajs/types"
import { isVariantInStock } from "@lib/util/product"
import { getSizeFromVariant } from "@modules/cart/utils"
import Modal from "@modules/common/components/modal"
import { Check } from "lucide-react"
import { useEffect, useState } from "react"

type SizeChangePopupProps = {
  isOpen: boolean
  close: () => void
  productId: string
  productName: string
  currentVariantId: string
  /** Variant IDs of all items currently in the cart */
  cartVariantIds?: string[]
  countryCode: string
  onSelectSize: (variantId: string) => void
}

const SizeChangePopup = ({
  isOpen,
  close,
  productId,
  productName,
  currentVariantId,
  cartVariantIds = [],
  countryCode,
  onSelectSize,
}: SizeChangePopupProps) => {
  const [variants, setVariants] = useState<HttpTypes.StoreProductVariant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch variant data when the modal opens
  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    setError(null)

    listProducts({
      countryCode,
      queryParams: { id: [productId], limit: 1 },
    })
      .then(({ response }) => {
        const product = response.products[0]
        if (product?.variants?.length) {
          setVariants(product.variants)
        } else {
          setError("No sizes available for this product.")
        }
      })
      .catch(() => {
        setError("Failed to load sizes. Please try again.")
      })
      .finally(() => setLoading(false))
  }, [isOpen, productId, countryCode])

  const handleSelect = (variantId: string) => {
    if (variantId === currentVariantId) {
      close()
      return
    }
    onSelectSize(variantId)
    close()
  }

  return (
    <Modal isOpen={isOpen} close={close} size="small">
      <Modal.Title>
        <div>
          Change Size
          {productName && (
            <p className="text-xs text-gray-500 mt-0.5 font-normal truncate max-w-[250px]">
              {productName}
            </p>
          )}
        </div>
      </Modal.Title>
      <Modal.Body>
        <div className="w-full pt-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-sm text-gray-500">
                Loading available sizes...
              </span>
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 text-center py-4">{error}</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                {variants.map((v) => {
                  const size = getSizeFromVariant(v)
                  const inStock = isVariantInStock(v)
                  const lowStock =
                    inStock &&
                    v.manage_inventory &&
                    !v.allow_backorder &&
                    v.inventory_quantity != null &&
                    v.inventory_quantity <= 5
                  const isSelected = v.id === currentVariantId
                  const isInCart =
                    !isSelected && cartVariantIds.includes(v.id)

                  return (
                    <button
                      key={v.id}
                      onClick={() => handleSelect(v.id)}
                      disabled={!inStock}
                      className={`
                        relative py-2.5 px-2 text-sm font-medium border rounded-lg transition-all
                        ${
                          isSelected
                            ? "border-brand-primary bg-brand-primary text-white"
                            : inStock
                              ? "border-gray-200 bg-white text-gray-900 hover:border-brand-primary hover:text-brand-primary"
                              : "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed"
                        }
                      `}
                    >
                      <span className="flex items-center justify-center gap-1">
                        {isSelected && (
                          <Check className="w-3.5 h-3.5 flex-shrink-0" />
                        )}
                        {size}
                      </span>
                      {!inStock && (
                        <span className="block text-[10px] leading-tight mt-0.5 font-normal">
                          Out of stock
                        </span>
                      )}
                      {inStock && lowStock && (
                        <span className="block text-[10px] leading-tight mt-0.5 font-normal text-amber-600">
                          Only {v.inventory_quantity} left
                        </span>
                      )}
                      {isInCart && inStock && (
                        <span className="block text-[10px] leading-tight mt-0.5 font-normal opacity-70">
                          In cart
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
              <p className="text-[11px] text-gray-400 mt-3 text-center">
                Stock is checked live for each size.
              </p>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default SizeChangePopup
